import { BookingPassengerDetail } from "@/redux/slices/clientBooking/types";
import { clsx } from "@/utils";
import { useMainPassenger } from "../useMainPassenger";
import { DetailRow } from "./DetailRow";
import { TripCoreRows } from "./TripCoreRows";

interface TripDetailsBodyProps {
  /** Other Details (DEV-446). */
  bookedBy?: string;
  reference?: string;
  additionalInfo?: string;
  /** Attachment file names — the draft variant maps its File[] to names; the priced variant passes names. */
  attachmentNames?: string[];
  passengers?: BookingPassengerDetail[];
  /** Primary tint for the section divider when the box is the active step (matches the box's border). */
  highlighted?: boolean;
}

/**
 * The shared Trip Details box body: the passenger / date / vehicle core rows (`TripCoreRows`, read
 * live from the draft) plus the optional Other Details rows (booked by / reference / notes /
 * attachments / extra passengers). Rendered identically by BOTH Booking Summary variants — the
 * draft-step summary and the priced payment-step card — so they can't drift (see the divider-highlight
 * bug that came from having two copies). The only per-variant difference is the data source, which the
 * caller resolves into these props.
 */
export const TripDetailsBody = ({ bookedBy, reference, additionalInfo, attachmentNames, passengers, highlighted }: TripDetailsBodyProps) => {
  // Passenger 1 is derived live from the selection so it shows without an explicit Proceed (like
  // Booked by) and stays in sync with "Who is this booking for?". The extra rows (#2+) are the
  // committed, optional entries from the Other Details panel.
  const mainPassenger = useMainPassenger();
  const extraPassengers = (passengers ?? [])
    .map((passenger, index) => ({ ...passenger, index }))
    .filter((passenger) => passenger.index > 0 && passenger.name.trim());
  const names = (attachmentNames ?? []).filter(Boolean);
  const hasOtherDetails = !!(bookedBy || reference || additionalInfo || names.length || mainPassenger.name || extraPassengers.length);

  return (
    <div className="flex flex-col gap-4">
      <TripCoreRows />
      {/* Match the box's active tint (border-primary) so the divider doesn't stay grey when highlighted. */}
      {hasOtherDetails && <div className={clsx("h-px", highlighted ? "bg-primary" : "bg-neutral-mid-gray")} />}
      {bookedBy && <DetailRow icon="User" title="Booked by" subtitle={bookedBy} />}
      {reference && <DetailRow icon="NoteText" title="Reference Number" subtitle={reference} />}
      {additionalInfo && <DetailRow icon="NoteText" title="Additional notes to your driver" subtitle={additionalInfo} />}
      {names.map((name, index) => (
        <DetailRow key={`attachment-${index}`} icon="DocumentText" title="Attachments" subtitle={name} />
      ))}
      {mainPassenger.name && <DetailRow icon="Profile2User" title={`${mainPassenger.name} (Passenger 1)`} subtitle={mainPassenger.phone} />}
      {extraPassengers.map((passenger) => (
        <DetailRow
          key={passenger.index}
          icon="Profile2User"
          title={`${passenger.name} (Passenger ${passenger.index + 1})`}
          subtitle={passenger.phone}
        />
      ))}
    </div>
  );
};
